import "./assets/main.css"
// import "./output.css"

import { createApp } from "vue"
import { createPinia } from "pinia"

import App from "./App.vue"
import router from "./router"

import * as Sentry from "@sentry/vue"

import PrimeVue from "primevue/config"
// -compat presets keep PrimeVue 4's rem sizing. PrimeVue 5 recalibrated the
// default presets for a 16px root font size; ours is already 16px, so the
// non-compat presets would shrink every component by ~12.5%.
import Lara from "@primeuix/themes/lara-compat"

import { PRIMEVUE_LICENSE } from "./primevueLicense"

import Tooltip from "primevue/tooltip"
import FocusTrap from "primevue/focustrap"
import KeyFilter from "primevue/keyfilter"

// import ToastService from "primevue/toastservice"
// import DialogService from "primevue/dialogservice"

import { FontAwesomeIcon, FontAwesomeLayers, FontAwesomeLayersText } from "@fortawesome/vue-fontawesome"
import "./fontAwesomeIcons"

import { initColorScheme } from "@/compositions/colorScheme"

// Apply the stored color scheme (light/dark/auto) before the app renders.
initColorScheme()

const app = createApp(App)

if (import.meta.env.MODE !== "development") {
  Sentry.init({
    app,
    dsn: "https://60ab22fcc5001893456f1fe80923b2af@o4508802893086720.ingest.us.sentry.io/4508802895052800",
    environment: import.meta.env.MODE, // This will set the environment based on your Node environment
    release: "babble@" + import.meta.env.VITE_APP_VERSION,
    // Enable logs to be sent to Sentry
    enableLogs: true,
    integrations: [
      // Sentry.consoleLoggingIntegration({ levels: ["log", "error"] }),
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.consoleLoggingIntegration({
        levels: ["log", "warn", "error"],
      }),
      Sentry.feedbackIntegration({
        showName: false,
        autoInject: false,
        useSentryUser: {
          email: "email",
          name: "username",
        },
      }),
    ],
    ignoreErrors: [
      // PrimeVue focustrap MutationObserver bug — harmless, just fails to shift focus
      /Cannot read properties of null \(reading 'nextSibling'\)/,
      // Contentless cross-origin / browser-extension noise: Firefox reports a
      // thrown `undefined` via window.onerror with no usable stack frames, and
      // Chrome reports cross-origin failures as "Script error." Neither maps to
      // our code (no bare throws in src/), so there's nothing to act on.
      /uncaught exception: undefined/i,
      "Script error.",
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^https:\/\/ibabbleon\.com\/dashboard/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.5, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })
}

app.use(createPinia())
app.use(router)
app.use(PrimeVue, {
  ripple: true,
  license: PRIMEVUE_LICENSE,
  // unstyled: true,
  // pt: Aura,
  // Default theme configuration
  theme: {
    preset: Lara,
    options: {
      prefix: "p",
      darkModeSelector: ".dark",
      cssLayer: false,
    },
  },
})
// app.use(ToastService)
// app.use(DialogService)

app.component("font-awesome-icon", FontAwesomeIcon)
app.component("font-awesome-layers", FontAwesomeLayers)
app.component("font-awesome-layers-text", FontAwesomeLayersText)

app.directive("tooltip", Tooltip)
app.directive("focustrap", FocusTrap)
app.directive("keyfilter", KeyFilter)

app.mount("#app")

// Add error handler for router
let handlingRouterError = false
router.onError((error) => {
  console.error("Router error:", error)
  // Avoid infinite loop if pushing "/" also fails
  if (!handlingRouterError) {
    handlingRouterError = true
    router.push("/").finally(() => {
      handlingRouterError = false
    })
  }
})
